import {
  usePinItemMutation,
  useUnpinItemMutation,
} from '@saaga/shared/store/message-pin-endpoints.store'
import { selectPageForConversation } from '@saaga/shared/store/slices/chat-selectors.store'
import { useAppSelector } from '@saaga/shared/store/store'
import { logger } from '@saaga/shared/utils/logger.util'
import { useCallback } from 'react'
import { toast } from 'sonner'

import type { ChatPinnedMessage } from '@/components/chat/chat-header.interface'
import type { ChatItem } from '@/components/chat/chat-message.interface'

import { buildPinSnapshot } from './pin-snapshot.helper'
import type { UseChatTitleEditReturn } from './use-chat-title-edit.hook'
import { useChatTitleEdit } from './use-chat-title-edit.hook'
import {
  PINNED_MESSAGES_INCLUDE_NOTES,
  PINNED_MESSAGES_PAGE_SIZE,
} from './use-pinned-messages.hook'

interface UseChatHeaderActionsParams {
  conversationId: string | null
  itemList: ChatItem[]
  pinnedMessageList: ChatPinnedMessage[]
  isItemPinned: (itemId: string) => boolean
  onPinnedMessageSelected: (pin: ChatPinnedMessage) => void
  onInvite?: () => void
  onLeave?: () => void
}

export interface UseChatHeaderActionsReturn extends UseChatTitleEditReturn {
  handleParticipantClick: (participantId: string) => void
  handlePinnedMessageClick: (messageId: string) => void
  handlePinItem: (itemId: string) => void
  handleSettings: () => void
  handleArchive: () => void
  handleInvite: () => void
  handleLeave: () => void
}

const toastPinError = (status: number | undefined): void => {
  if (status === 409) {
    toast.error('Pin limit reached (50). Unpin older items to add more.')
    return
  }
  if (status === 403) {
    toast.error('Only the person who pinned this can unpin it.')
    return
  }
  if (status === 422) {
    toast.error('This item could not be pinned (invalid snapshot).')
    return
  }
  toast.error('Could not update pin.')
}

type PinMutationTrigger = ReturnType<typeof usePinItemMutation>[0]
type UnpinMutationTrigger = ReturnType<typeof useUnpinItemMutation>[0]

interface TogglePinParams {
  conversationId: string
  item: ChatItem
  isPinned: boolean
  currentPage: number
  pinItem: PinMutationTrigger
  unpinItem: UnpinMutationTrigger
}

const togglePinForItem = async (params: TogglePinParams): Promise<void> => {
  if (params.isPinned) {
    await params
      .unpinItem({
        conversationId: params.conversationId,
        itemId: params.item.id,
        pageSize: PINNED_MESSAGES_PAGE_SIZE,
        includeNotes: PINNED_MESSAGES_INCLUDE_NOTES,
      })
      .unwrap()
    return
  }
  const snapshot = buildPinSnapshot(params.item)
  await params
    .pinItem({
      conversationId: params.conversationId,
      itemId: params.item.id,
      body: {
        parent_message_id: snapshot.parentMessageId,
        item_preview: snapshot.itemPreview,
        item_role: snapshot.itemRole,
      },
      pageSize: PINNED_MESSAGES_PAGE_SIZE,
      includeNotes: PINNED_MESSAGES_INCLUDE_NOTES,
      // The item the user just clicked is in the virtualized list, so it
      // lives on (or before) the current page. Using that avoids a
      // page-1 snap if the user clicks the pin dropdown row before the
      // server POST resolves.
      optimisticPageNumber: params.currentPage,
    })
    .unwrap()
}

const usePinToggleHandler = (
  conversationId: string | null,
  itemList: ChatItem[],
  isItemPinned: (itemId: string) => boolean
): ((itemId: string) => Promise<void>) => {
  const [pinItem] = usePinItemMutation()
  const [unpinItem] = useUnpinItemMutation()
  const currentPage = useAppSelector((state) =>
    conversationId ? selectPageForConversation(state, conversationId) : 1
  )
  return useCallback(
    async (itemId: string): Promise<void> => {
      if (!conversationId) return
      const item = itemList.find((candidate) => candidate.id === itemId)
      if (!item) return
      try {
        await togglePinForItem({
          conversationId,
          item,
          isPinned: isItemPinned(itemId),
          currentPage,
          pinItem,
          unpinItem,
        })
      } catch (err) {
        logger.error('pin toggle failed', err)
        toastPinError((err as { status?: number })?.status)
      }
    },
    [conversationId, itemList, isItemPinned, currentPage, pinItem, unpinItem]
  )
}

export const useChatHeaderActions = (
  params: UseChatHeaderActionsParams
): UseChatHeaderActionsReturn => {
  const { conversationId, itemList, pinnedMessageList, isItemPinned, onPinnedMessageSelected } =
    params
  const titleEdit = useChatTitleEdit({ conversationId })
  const handlePinItem = usePinToggleHandler(conversationId, itemList, isItemPinned)

  const handleParticipantClick = useCallback((id: string) => logger.debug('Participant:', id), [])
  const handlePinnedMessageClick = useCallback(
    (messageId: string): void => {
      const pin = pinnedMessageList.find((candidate) => candidate.id === messageId)
      if (pin) onPinnedMessageSelected(pin)
    },
    [pinnedMessageList, onPinnedMessageSelected]
  )
  const handleSettings = useCallback(() => logger.debug('Chat settings clicked'), [])
  const handleArchive = useCallback(() => logger.debug('Archive chat clicked'), [])
  const handleInvite = useCallback(() => params.onInvite?.(), [params.onInvite])
  const handleLeave = useCallback(() => params.onLeave?.(), [params.onLeave])

  return {
    ...titleEdit,
    handleParticipantClick,
    handlePinnedMessageClick,
    handlePinItem,
    handleSettings,
    handleArchive,
    handleInvite,
    handleLeave,
  }
}
